import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';
import LanguageToggle from '../components/LanguageToggle';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import { useLanguage } from '../contexts/LanguageContext';
import { apiClient } from '../lib/apiClient';
import {
  Plus, X, Edit3, Trash2, Package, FolderTree, LogOut, AlertCircle, Loader2, Eye,
} from 'lucide-react';

// Default deadline for COP31 Antalya cycle: 15 June 2026
const DEFAULT_DEADLINE = '2026-06-15';

const emptyCat = () => ({ name: '', name_tr: '', name_en: '', icon: '', sort_order: 0, is_active: true, deadline: DEFAULT_DEADLINE });
const emptyProd = () => ({
  category_id: '', title: '', title_tr: '', title_en: '',
  description: '', description_tr: '', description_en: '',
  price: 0, currency: 'USD', unit_type: 'piece',
  min_quantity: 1, max_quantity: 999, deadline: DEFAULT_DEADLINE,
  is_active: true, approval_required: false, stand_allocation_required: true,
  eligibility_zones: [], eligibility_hall_ids: [], eligibility_stand_types: [],
});

// ── DatePicker: 3 independent dropdowns (Day/Month/Year) ──
// Keeps its own internal state so partial selections aren't lost.
// Only emits a "YYYY-MM-DD" string to the parent when ALL three are picked.
// If the parent passes a value, that seeds the internal state exactly once.
function DatePicker({ value, onChange, label, testid }) {
  const [d, setD] = React.useState(() => (value || '').split('-')[2] ? Number((value || '').split('-')[2]) : '');
  const [m, setM] = React.useState(() => (value || '').split('-')[1] ? Number((value || '').split('-')[1]) : '');
  const [y, setY] = React.useState(() => (value || '').split('-')[0] ? Number((value || '').split('-')[0]) : '');

  // Re-seed when the outside value changes (e.g. modal reopens for a
  // different row, or category inheritance fires).
  React.useEffect(() => {
    const [yy, mm, dd] = (value || '').split('-');
    setY(yy ? Number(yy) : '');
    setM(mm ? Number(mm) : '');
    setD(dd ? Number(dd) : '');
  }, [value]);

  const emitIfComplete = (nd, nm, ny) => {
    if (nd && nm && ny) {
      onChange(`${ny}-${String(nm).padStart(2, '0')}-${String(nd).padStart(2, '0')}`);
    } else if (!nd && !nm && !ny) {
      onChange('');
    }
  };

  const now = new Date().getFullYear();
  const years = Array.from({ length: 7 }, (_, i) => now - 1 + i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const daysInMonth = (yy, mm) => (yy && mm ? new Date(yy, mm, 0).getDate() : 31);
  const days = Array.from({ length: daysInMonth(y || now, m || 1) }, (_, i) => i + 1);

  const sel = 'flex-1 border border-gray-200 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:border-purple-400';
  return (
    <div>
      {label && <label className="text-xs font-semibold text-gray-600 mb-1 block">{label}</label>}
      <div className="flex gap-2" data-testid={testid}>
        <select className={sel} value={d || ''} onChange={e => { const v = parseInt(e.target.value) || ''; setD(v); emitIfComplete(v, m, y); }}>
          <option value="">Day</option>
          {days.map(x => <option key={x} value={x}>{x}</option>)}
        </select>
        <select className={sel} value={m || ''} onChange={e => { const v = parseInt(e.target.value) || ''; setM(v); emitIfComplete(d, v, y); }}>
          <option value="">Month</option>
          {months.map(x => <option key={x} value={x}>{x}</option>)}
        </select>
        <select className={sel} value={y || ''} onChange={e => { const v = parseInt(e.target.value) || ''; setY(v); emitIfComplete(d, m, v); }}>
          <option value="">Year</option>
          {years.map(x => <option key={x} value={x}>{x}</option>)}
        </select>
        {(d || m || y) && (
          <button onClick={() => { setD(''); setM(''); setY(''); onChange(''); }} type="button" className="text-xs text-gray-400 hover:text-red-500 px-2">✕</button>
        )}
      </div>
    </div>
  );
}

export default function UltraAdminServiceCatalogPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tr = language === 'tr';
  const t = (en, trTxt) => (tr ? trTxt : en);

  const [tab, setTab] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [catForm, setCatForm] = useState(null); // null or emptyCat()
  const [prodForm, setProdForm] = useState(null);
  const [previewProd, setPreviewProd] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        apiClient.get('/ultra-admin/service-categories'),
        apiClient.get('/ultra-admin/service-products'),
      ]);
      setCategories(c.data);
      setProducts(p.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    setLoading(false);
  };
  useEffect(() => { fetchAll(); }, []);

  const saveCat = async () => {
    setSaving(true); setError(null);
    try {
      if (catForm.id) await apiClient.put(`/ultra-admin/service-categories/${catForm.id}`, catForm);
      else await apiClient.post('/ultra-admin/service-categories', catForm);
      setCatForm(null);
      await fetchAll();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    setSaving(false);
  };
  const delCat = async (id) => {
    if (!window.confirm(t('Delete this category?','Bu kategoriyi sil?'))) return;
    try { await apiClient.delete(`/ultra-admin/service-categories/${id}`); await fetchAll(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const saveProd = async () => {
    setSaving(true); setError(null);
    try {
      const payload = { ...prodForm, price: parseFloat(prodForm.price) || 0,
        min_quantity: parseInt(prodForm.min_quantity) || 1,
        max_quantity: parseInt(prodForm.max_quantity) || 999 };
      if (prodForm.id) await apiClient.put(`/ultra-admin/service-products/${prodForm.id}`, payload);
      else await apiClient.post('/ultra-admin/service-products', payload);
      setProdForm(null);
      await fetchAll();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    setSaving(false);
  };
  const delProd = async (id) => {
    if (!window.confirm(t('Delete this product?','Bu ürünü sil?'))) return;
    try { await apiClient.delete(`/ultra-admin/service-products/${id}`); await fetchAll(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  // Number formatter with thousand separator + 2 decimal places (locale-aware).
  const fmtMoney = (v, cur) => {
    const n = typeof v === 'number' ? v : Number(v) || 0;
    return new Intl.NumberFormat(tr ? 'tr-TR' : 'en-US', {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(n) + ' ' + (cur || '');
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 flex flex-col">
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">{t('Service Catalog','Servis Kataloğu')}</h1>
            <p className="text-xs text-gray-500 mt-0.5">{t('Manage service categories and products offered to allocated exhibitors','Stand atanan katılımcılara sunulacak servis kategorileri ve ürünleri')}</p>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <button onClick={() => { localStorage.clear(); navigate('/giris'); }} className="text-gray-400 hover:text-red-500">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 flex-1">
          <PageIntro {...getPageIntro('service-catalog', language)} />
          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-gray-200">
            <button onClick={() => setTab('categories')} data-testid="tab-categories"
              className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors ${tab === 'categories' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              <FolderTree className="w-4 h-4" /> {t('Categories','Kategoriler')} <span className="text-xs text-gray-400">({categories.length})</span>
            </button>
            <button onClick={() => setTab('products')} data-testid="tab-products"
              className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors ${tab === 'products' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              <Package className="w-4 h-4" /> {t('Products','Ürünler')} <span className="text-xs text-gray-400">({products.length})</span>
            </button>
          </div>

          {loading && <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('Loading','Yükleniyor...')}</div>}

          {/* CATEGORIES */}
          {tab === 'categories' && !loading && (
            <>
              <div className="flex justify-end mb-4">
                <button onClick={() => setCatForm(emptyCat())} data-testid="new-category-btn"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-md"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                  <Plus className="w-4 h-4" /> {t('New Category','Yeni Kategori')}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map(c => (
                  <div key={c.id} className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-bold text-gray-800">{tr && c.name_tr ? c.name_tr : (c.name_en || c.name)}</h3>
                        <p className="text-xs text-gray-400">{c.product_count} {t('products','ürün')}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {c.is_active ? t('Active','Aktif') : t('Inactive','Pasif')}
                      </span>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                      <button onClick={() => setCatForm(c)} className="flex-1 text-xs py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center justify-center gap-1">
                        <Edit3 className="w-3 h-3" /> {t('Edit','Düzenle')}
                      </button>
                      <button onClick={() => delCat(c.id)} className="flex-1 text-xs py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center gap-1">
                        <Trash2 className="w-3 h-3" /> {t('Delete','Sil')}
                      </button>
                    </div>
                  </div>
                ))}
                {categories.length === 0 && <p className="text-gray-400 col-span-full text-center py-8">{t('No categories yet.','Henüz kategori yok.')}</p>}
              </div>
            </>
          )}

          {/* PRODUCTS */}
          {tab === 'products' && !loading && (
            <>
              <div className="flex justify-end mb-4">
                <button onClick={() => {
                  const firstCat = categories[0];
                  setProdForm({ ...emptyProd(), category_id: firstCat?.id || '', deadline: firstCat?.deadline || '' });
                }}
                  disabled={categories.length === 0} data-testid="new-product-btn"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-md disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                  <Plus className="w-4 h-4" /> {t('New Product','Yeni Ürün')}
                </button>
              </div>
              {categories.length === 0 && <p className="text-amber-600 text-sm mb-3">{t('Create at least one category first.','Önce en az bir kategori oluşturun.')}</p>}
              <div className="space-y-5">
                {[...categories]
                  .sort((a, b) => {
                    // Categories with products first, then by sort_order
                    const ap = products.filter(p => p.category_id === a.id).length;
                    const bp = products.filter(p => p.category_id === b.id).length;
                    if ((ap > 0) !== (bp > 0)) return bp - ap;       // non-empty wins
                    if (ap !== bp) return bp - ap;                    // more products first
                    return (a.sort_order || 0) - (b.sort_order || 0); // tie-break
                  })
                  .map(cat => {
                  const catProducts = products.filter(p => p.category_id === cat.id);
                  return (
                    <div key={cat.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                        <h3 className="font-bold text-gray-800 text-base">{tr && cat.name_tr ? cat.name_tr : (cat.name_en || cat.name)}</h3>
                        <span className="text-xs text-gray-500">{catProducts.length} {t('products','ürün')}</span>
                      </div>
                      {catProducts.length === 0 ? (
                        <p className="text-gray-400 text-sm text-center py-6">{t('No products in this category.','Bu kategoride ürün yok.')}</p>
                      ) : (
                        <table className="w-full text-sm table-fixed">
                          <thead className="bg-gray-50 text-gray-600">
                            <tr>
                              <th className="px-4 py-2 text-left">{t('Title','Başlık')}</th>
                              <th className="px-4 py-2 text-right w-40 tabular-nums">{t('Price','Fiyat')}</th>
                              <th className="px-4 py-2 text-center w-24">{t('Status','Durum')}</th>
                              <th className="px-4 py-2 w-28"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {catProducts.map(p => (
                              <tr key={p.id} className="border-t border-gray-100">
                                <td className="px-4 py-2">
                                  <div className="font-semibold text-gray-800">{tr && p.title_tr ? p.title_tr : (p.title_en || p.title)}</div>
                                  {p.approval_required && <span className="text-xs text-amber-600">⚠ {t('Approval required','Onay gerekli')}</span>}
                                </td>
                                <td className="px-4 py-2 text-right font-semibold tabular-nums whitespace-nowrap">{fmtMoney(p.price, p.currency)}</td>
                                <td className="px-4 py-2 text-center">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                                    {p.is_active ? t('Active','Aktif') : t('Inactive','Pasif')}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-right">
                                  <button onClick={() => setPreviewProd(p)} title={t('Preview','Önizleme')} className="text-gray-500 hover:text-blue-600 mx-1" data-testid={`preview-${p.id}`}><Eye className="w-4 h-4" /></button>
                                  <button onClick={() => setProdForm(p)} className="text-gray-500 hover:text-purple-600 mx-1"><Edit3 className="w-4 h-4" /></button>
                                  <button onClick={() => delProd(p.id)} className="text-gray-500 hover:text-red-600 mx-1"><Trash2 className="w-4 h-4" /></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
                {categories.length > 0 && products.length === 0 && (
                  <p className="text-center text-gray-400 py-8">{t('No products yet. Click "New Product" above.','Henüz ürün yok. Yukarıdaki "Yeni Ürün" butonuna tıklayın.')}</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* CATEGORY MODAL */}
      {catForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">{catForm.id ? t('Edit Category','Kategori Düzenle') : t('New Category','Yeni Kategori')}</h2>
              <button onClick={() => setCatForm(null)} className="text-gray-400"><X className="w-5 h-5" /></button>
            </div>
            {error && <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 text-xs text-red-700 mb-3 flex gap-2"><AlertCircle className="w-4 h-4" /> {String(error)}</div>}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <Input label={t('Name (Türkçe)','Ad (Türkçe)')} value={catForm.name_tr || ''} onChange={v => setCatForm(f => ({ ...f, name_tr: v, name: v || f.name_en || f.name }))} testid="cat-name-tr" />
                <Input label={t('Name (English)','Ad (English)')} value={catForm.name_en || ''} onChange={v => setCatForm(f => ({ ...f, name_en: v, name: f.name_tr || v || f.name }))} testid="cat-name-en" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input label={t('Icon (optional)','Simge (opsiyonel)')} value={catForm.icon || ''} onChange={v => setCatForm(f => ({ ...f, icon: v }))} />
                <Input label={t('Sort order','Sıra')} type="number" value={catForm.sort_order} onChange={v => setCatForm(f => ({ ...f, sort_order: parseInt(v) || 0 }))} />
              </div>
              <DatePicker label={t('Default deadline (applies to new products)','Varsayılan son tarih (yeni ürünlere uygulanır)')}
                value={catForm.deadline || ''}
                onChange={v => setCatForm(f => ({ ...f, deadline: v }))}
                testid="cat-deadline" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={catForm.is_active} onChange={e => setCatForm(f => ({ ...f, is_active: e.target.checked }))} /> {t('Active','Aktif')}</label>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setCatForm(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm">{t('Cancel','İptal')}</button>
              <button onClick={saveCat} disabled={saving || !(catForm.name_tr || catForm.name_en)} data-testid="save-category-btn" className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>{saving ? '...' : t('Save','Kaydet')}</button>
            </div>
          </div>
        </div>
      )}

      {/* PRODUCT MODAL */}
      {prodForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl p-6 my-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">{prodForm.id ? t('Edit Product','Ürün Düzenle') : t('New Product','Yeni Ürün')}</h2>
              <button onClick={() => setProdForm(null)} className="text-gray-400"><X className="w-5 h-5" /></button>
            </div>
            {error && <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 text-xs text-red-700 mb-3 flex gap-2"><AlertCircle className="w-4 h-4" /> {String(error)}</div>}
            <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">{t('Category','Kategori')} *</label>
                <select value={prodForm.category_id} onChange={e => {
                    const newCatId = e.target.value;
                    const newCat = categories.find(c => c.id === newCatId);
                    // Inherit category deadline ONLY when current product.deadline is empty
                    setProdForm(f => ({
                      ...f,
                      category_id: newCatId,
                      deadline: f.deadline || newCat?.deadline || '',
                    }));
                  }}
                  data-testid="prod-category"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm">
                  {categories.map(c => <option key={c.id} value={c.id}>{tr && c.name_tr ? c.name_tr : c.name}</option>)}
                </select>
              </div>
              <Input label={t('Title (Türkçe)','Başlık (Türkçe)')} value={prodForm.title_tr || ''} onChange={v => setProdForm(f => ({ ...f, title_tr: v, title: v || f.title_en || f.title }))} testid="prod-title-tr" />
              <Input label={t('Title (English)','Başlık (English)')} value={prodForm.title_en || ''} onChange={v => setProdForm(f => ({ ...f, title_en: v, title: f.title_tr || v || f.title }))} testid="prod-title-en" />
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">{t('Description (Türkçe)','Açıklama (Türkçe)')}</label>
                <textarea rows="2" value={prodForm.description_tr || ''} onChange={e => setProdForm(f => ({ ...f, description_tr: e.target.value, description: e.target.value || f.description_en || f.description }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">{t('Description (English)','Açıklama (English)')}</label>
                <textarea rows="2" value={prodForm.description_en || ''} onChange={e => setProdForm(f => ({ ...f, description_en: e.target.value, description: f.description_tr || e.target.value || f.description }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input label={t('Price','Fiyat')} type="number" value={prodForm.price} onChange={v => setProdForm(f => ({ ...f, price: v }))} testid="prod-price" />
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">{t('Currency','Para Birimi')}</label>
                  <select value={prodForm.currency} onChange={e => setProdForm(f => ({ ...f, currency: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm">
                    {['USD', 'EUR', 'TRY', 'GBP'].map(x => <option key={x}>{x}</option>)}
                  </select>
                </div>
                <Input label={t('Unit','Birim')} value={prodForm.unit_type} onChange={v => setProdForm(f => ({ ...f, unit_type: v }))} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input label={t('Min Qty','Min Adet')} type="number" value={prodForm.min_quantity} onChange={v => setProdForm(f => ({ ...f, min_quantity: v }))} />
                <Input label={t('Max Qty','Max Adet')} type="number" value={prodForm.max_quantity} onChange={v => setProdForm(f => ({ ...f, max_quantity: v }))} />
              </div>
              <DatePicker label={t('Deadline (inherits from category if empty)','Son tarih (boşsa kategori varsayılanı kullanılır)')}
                value={prodForm.deadline || ''}
                onChange={v => setProdForm(f => ({ ...f, deadline: v }))}
                testid="prod-deadline" />
              <div className="bg-gray-50 rounded-xl p-3 space-y-2 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={prodForm.is_active} onChange={e => setProdForm(f => ({ ...f, is_active: e.target.checked }))} /> {t('Active','Aktif')}</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={prodForm.approval_required} onChange={e => setProdForm(f => ({ ...f, approval_required: e.target.checked }))} /> {t('Requires admin approval','Admin onayı gerekir')}</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={prodForm.stand_allocation_required} onChange={e => setProdForm(f => ({ ...f, stand_allocation_required: e.target.checked }))} /> {t('Requires stand allocation','Stand ataması gerekir')}</label>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">{t('Eligible zones (comma-separated: blue_zone,green_zone; empty=all)','Uygun zone\'lar (virgülle ayır; boş=tümü)')}</label>
                <input value={(prodForm.eligibility_zones || []).join(',')}
                  onChange={e => setProdForm(f => ({ ...f, eligibility_zones: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" placeholder="blue_zone,green_zone" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setProdForm(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm">{t('Cancel','İptal')}</button>
              <button onClick={saveProd} disabled={saving || !(prodForm.title_tr || prodForm.title_en) || !prodForm.category_id} data-testid="save-product-btn" className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>{saving ? '...' : t('Save','Kaydet')}</button>
            </div>
          </div>
        </div>
      )}

      {/* PRODUCT PREVIEW MODAL (read-only) */}
      {previewProd && (() => {
        const p = previewProd;
        const cat = categories.find(c => c.id === p.category_id);
        const Field = ({ label, value }) => (
          <div className="bg-gray-50 rounded-lg px-3 py-2">
            <div className="text-xs text-gray-500">{label}</div>
            <div className="font-semibold text-gray-800 text-sm break-words">{value || '—'}</div>
          </div>
        );
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-auto">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-4">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-bold text-gray-800">{t('Product Preview','Ürün Önizleme')}</h2>
                </div>
                <button onClick={() => setPreviewProd(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Hero */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-5 border border-purple-100">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="text-xs text-purple-700 font-semibold uppercase tracking-wide mb-1">{cat ? (tr && cat.name_tr ? cat.name_tr : cat.name) : '—'}</p>
                      <h3 className="text-xl font-bold text-gray-900">{tr && p.title_tr ? p.title_tr : (p.title_en || p.title)}</h3>
                      {(p.title_en || p.title_tr) && (
                        <p className="text-sm text-gray-500 mt-0.5">{tr ? p.title_en : p.title_tr}</p>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                      {p.is_active ? t('Active','Aktif') : t('Inactive','Pasif')}
                    </span>
                  </div>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-gray-900 tabular-nums">{fmtMoney(p.price, '')}</span>
                    <span className="text-sm text-gray-500">{p.currency} / {p.unit_type}</span>
                  </div>
                </div>

                {/* Description */}
                {(p.description || p.description_tr || p.description_en) && (
                  <div>
                    <div className="text-xs font-semibold text-gray-600 mb-1">{t('Description','Açıklama')}</div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{tr && p.description_tr ? p.description_tr : (p.description_en || p.description)}</p>
                  </div>
                )}

                {/* Specs grid */}
                <div className="grid grid-cols-2 gap-2">
                  <Field label={t('Min Qty','Min Adet')} value={p.min_quantity} />
                  <Field label={t('Max Qty','Max Adet')} value={p.max_quantity} />
                  <Field label={t('Deadline','Son Tarih')} value={p.deadline} />
                  <Field label={t('Currency','Para Birimi')} value={p.currency} />
                </div>

                {/* Flags */}
                <div className="flex flex-wrap gap-2 text-xs">
                  {p.approval_required && <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full">⚠ {t('Approval required','Onay gerekir')}</span>}
                  {p.stand_allocation_required && <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">📍 {t('Stand allocation required','Stand ataması gerekir')}</span>}
                </div>

                {/* Eligibility */}
                {(p.eligibility_zones?.length > 0 || p.eligibility_hall_ids?.length > 0) && (
                  <div className="bg-gray-50 rounded-xl p-3">
                    <div className="text-xs font-semibold text-gray-600 mb-2">{t('Eligibility','Uygunluk')}</div>
                    {p.eligibility_zones?.length > 0 && (
                      <div className="text-xs text-gray-700"><strong>{t('Zones','Zone\'lar')}:</strong> {p.eligibility_zones.join(', ')}</div>
                    )}
                    {p.eligibility_hall_ids?.length > 0 && (
                      <div className="text-xs text-gray-700"><strong>Halls:</strong> {p.eligibility_hall_ids.join(', ')}</div>
                    )}
                    {(!p.eligibility_zones?.length && !p.eligibility_hall_ids?.length) && (
                      <div className="text-xs text-gray-500 italic">{t('All zones and halls','Tüm zone ve hall\'lar')}</div>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button onClick={() => setPreviewProd(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold">{t('Close','Kapat')}</button>
                  <button onClick={() => { setPreviewProd(null); setProdForm(p); }} data-testid="preview-edit-btn" className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-2" style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                    <Edit3 className="w-4 h-4" /> {t('Edit','Düzenle')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', placeholder, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600 mb-1 block">{label}</label>
      <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder || ''}
        data-testid={testid}
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-400" />
    </div>
  );
}
